import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n";
import { Panel } from "@/components/ui/panel";
import { MySkillsPicker } from "@/components/profile/my-skills-picker";
import { SettingsPageShell } from "@/components/shared/settings-page-shell";
import { useStore } from "@/stores/store-context";

export const ProfileSkillsPage = observer(function ProfileSkillsPage() {
  const { t } = useTranslation();
  const { router, workspace, workspaceSkills, userSkills } = useStore();
  const [activeSlug, setActiveSlug] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        if (workspace.workspaces.length === 0) {
          await workspace.fetchWorkspaces();
        }
        if (cancelled) return;

        const slugFromRoute = router.workspaceSlug;
        const slug =
          (slugFromRoute && workspace.workspaces.find((ws) => ws.slug === slugFromRoute)?.slug) ||
          workspace.workspaces[0]?.slug ||
          null;

        if (!slug) {
          setActiveSlug(null);
          return;
        }

        if (cancelled) return;

        setActiveSlug(slug);

        await Promise.all([
          workspaceSkills.fetchSkills(slug, { archived: false }),
          userSkills.fetchMySkills(slug),
        ]);
      } catch {
        if (!cancelled) toast.error(t("profile.skills.load_error"));
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [router.workspaceSlug, workspace, workspaceSkills, userSkills]);

  return (
    <SettingsPageShell
      title={t("profile.skills.title")}
      description={t("profile.skills.description")}
    >
      {!activeSlug ? (
        <Panel>
          <p className="text-sm text-muted-foreground">{t("profile.skills.no_workspace")}</p>
        </Panel>
      ) : (
        <MySkillsPicker
          workspaceSlug={activeSlug}
          skills={workspaceSkills.getSkillsForSlug(activeSlug)}
        />
      )}
    </SettingsPageShell>
  );
});
